import { observability } from "./observability"

const SESSION_ID_KEY = "jfrader.analytics.sessionId"
const VISIT_LOGGED_KEY = "jfrader.analytics.visitLogged"
const REFERRAL_KEY = "jfrader.analytics.referrals"
const TIP_LOGGED_PREFIX = "jfrader.analytics.tip."
const REFERRAL_FIELDS = ["ref", "referer", "referrer", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
const PAYMENT_METHODS = new Set(["bitcoin", "lightning"])

let memorySessionId = ""
let memoryVisitLogged = false
let memoryReferrals: Record<string, string> | null = null
const memoryTipLogs = new Set<string>()

function getSessionStorage() { try { return window.sessionStorage } catch { return null } }
function getStoredItem(key: string) { try { return getSessionStorage()?.getItem(key) || "" } catch { return undefined } }
function setStoredItem(key: string, value: string) { try { getSessionStorage()?.setItem(key, value); return true } catch { return false } }
function removeStoredItem(key: string) { try { getSessionStorage()?.removeItem(key) } catch {} }

function createSessionId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "")
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

function getSessionId() {
  const ex = getStoredItem(SESSION_ID_KEY)
  if (ex) return ex
  if (ex === undefined && memorySessionId) return memorySessionId
  const id = createSessionId()
  if (!setStoredItem(SESSION_ID_KEY, id)) memorySessionId = id
  return id
}

function getCurrentPath() { return `${location.pathname}${location.search}${location.hash}` }

function getReferralParams() {
  const p = new URLSearchParams(location.search)
  return REFERRAL_FIELDS.reduce((acc: any, f) => { const v = p.get(f); if (v) acc[f] = v; return acc }, {})
}

function getInitialReferrals() {
  const ex = getStoredItem(REFERRAL_KEY)
  if (ex) { try { return JSON.parse(ex) } catch { removeStoredItem(REFERRAL_KEY) } }
  if (ex === undefined && memoryReferrals) return memoryReferrals
  const refs = getReferralParams()
  if (!setStoredItem(REFERRAL_KEY, JSON.stringify(refs))) memoryReferrals = refs
  return refs
}

function createEventPayload(fields: Record<string, unknown> = {}) {
  return { sessionId: getSessionId(), path: getCurrentPath(), referrer: document.referrer || "", referrals: getInitialReferrals(), ...fields }
}

function sendEvent(endpoint: string, payload: unknown) {
  return fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true, body: JSON.stringify(payload) }).catch(() => undefined)
}

function trackPosthogEvent(event: string, properties?: Record<string, unknown>) {
  try { observability.track(event, properties) } catch {}
}

function logVisit() {
  const v = getStoredItem(VISIT_LOGGED_KEY)
  if (v || (v === undefined && memoryVisitLogged)) return
  if (!setStoredItem(VISIT_LOGGED_KEY, "1")) memoryVisitLogged = true
  trackPosthogEvent("site_visit")
  sendEvent("/api/v1/events/visit", createEventPayload())
}

function getTipLogKey({ address, method, paymentHash, status }: any) {
  if (!PAYMENT_METHODS.has(method)) return ""
  const tipId = method === "lightning" ? paymentHash : address
  if (!tipId) return ""
  return `${TIP_LOGGED_PREFIX}${method}.${status}.${tipId}`
}
function markTipLogged(key: string) {
  const t = key ? getStoredItem(key) : ""
  if (!key || t || (t === undefined && memoryTipLogs.has(key))) return false
  if (!setStoredItem(key, "1")) memoryTipLogs.add(key)
  return true
}

function logTipStarted({ address, method, paymentHash }: any) {
  const key = getTipLogKey({ address, method, paymentHash, status: "started" })
  if (!markTipLogged(key)) return
  trackPosthogEvent("tip_started", { payment_method: method })
}

function logTipEvent({ address, method, paymentHash, status }: any) {
  const key = getTipLogKey({ address, method, paymentHash, status })
  if (!markTipLogged(key)) return
  if (status === "paid") trackPosthogEvent("tip_completed", { payment_method: method })
  sendEvent("/api/v1/events/tip", createEventPayload({ address, method, paymentHash, status }))
}

export { createEventPayload, getInitialReferrals, getSessionId, logTipEvent, logTipStarted, logVisit }
